import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TimezoneContextType {
  timezone: string;
  locale: string;
  setTimezone: (timezone: string) => void;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const [timezone, setTimezoneState] = useState<string>('');
  const [locale, setLocale] = useState<string>('');

  useEffect(() => {
    // Get browser timezone and locale
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    
    // Check localStorage for saved preferences
    const savedTimezone = localStorage.getItem('userTimezone');
    const savedLocale = localStorage.getItem('userLocale');
    
    setTimezoneState(savedTimezone || browserTimezone);
    setLocale(savedLocale || browserLocale);
  }, []);

  const setTimezone = (newTimezone: string) => {
    setTimezoneState(newTimezone);
    localStorage.setItem('userTimezone', newTimezone);
  };


  const value: TimezoneContextType = {
    timezone,
    locale,
    setTimezone,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};
